"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useLocale } from "next-intl";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  contactFormSchema,
  contactNeedValues,
  type ContactField,
  type ContactNeed,
} from "@/lib/validations/contact";
import {
  TurnstileWidget,
  isTurnstileEnabled,
} from "@/components/TurnstileWidget";
import { trackEvent } from "@/lib/track";
import { localizedRoutes } from "@/lib/routes/routes";

const FORM_ID = "contatti_5";
const CONTACT_EMAIL = "dariomarcobellini@dimensione4.it";
const WHATSAPP_URL = "https://wa.me/393334404903";
const DRAFT_KEY = "d4_contact_draft";
// How long a click on "Invia" waits for Turnstile before giving up.
const TOKEN_WAIT_MS = 6000;

type Values = Record<Exclude<ContactField, "need">, string> & {
  need: ContactNeed | "";
};

const EMPTY: Values = { name: "", email: "", phone: "", need: "", message: "" };
const FIELDS: ContactField[] = ["name", "email", "phone", "need", "message"];
const FIELD_NAMES: Record<ContactField, string> = {
  name: "nome",
  email: "email",
  phone: "telefono",
  need: "cosa ti serve",
  message: "messaggio",
};

const NEED_LABELS: Record<ContactNeed, [it: string, en: string]> = {
  new_website: ["Un sito nuovo o un negozio online", "A new website or online shop"],
  website_redesign: ["Rifare o velocizzare il sito che ho", "Redo or speed up my current site"],
  mvp: ["Un software su misura o un MVP", "Custom software or an MVP"],
  ai_automation: ["Automazioni e AI per risparmiare tempo", "Automation and AI to save time"],
  support_maintenance: ["Assistenza continua sul mio sito", "Ongoing support for my site"],
  other: ["Altro, ne parliamo", "Something else, let's talk"],
};

type SubmitError = { message: string; kind: "antibot" | "rate_limit" | "server" };

function validate(values: Values) {
  const r = contactFormSchema.safeParse(values);
  const errors: Partial<Record<ContactField, string>> = {};
  if (!r.success) {
    for (const issue of r.error.issues) {
      const f = issue.path[0] as ContactField;
      errors[f] ??= issue.message;
    }
  }
  return { data: r.success ? r.data : null, errors };
}

function focusField(field: ContactField) {
  const el = document.querySelector<HTMLElement>(
    field === "need" ? 'input[name="need"]' : `#cf-${field}`,
  );
  el?.focus();
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function ContactForm() {
  const router = useRouter();
  const locale = useLocale();
  const isItalian = locale.startsWith("it");
  const t = (it: string, en: string) => (isItalian ? it : en);
  const routes = localizedRoutes[isItalian ? "it" : "en"];

  const [values, setValues] = useState<Values>(EMPTY);
  const [touched, setTouched] = useState<Partial<Record<ContactField, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const turnstileEnabled = isTurnstileEnabled();
  const tokenRef = useRef<string | null>(null);
  // Bumping the key remounts the widget: a Turnstile token is single-use.
  const [turnstileKey, setTurnstileKey] = useState(0);
  const handleTurnstileToken = useCallback((token: string | null) => {
    tokenRef.current = token;
  }, []);

  // Draft survives a reload. Storage can throw (private mode): ignore it.
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) setValues({ ...EMPTY, ...JSON.parse(saved) });
    } catch {}
  }, []);
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(values));
    } catch {}
  }, [values]);

  const { errors } = validate(values);
  const errorOf = (f: ContactField) => (touched[f] ? errors[f] : undefined);
  const set = (f: ContactField, v: string) => setValues((prev) => ({ ...prev, [f]: v }));
  const touch = (f: ContactField) => setTouched((prev) => ({ ...prev, [f]: true }));

  const waitForToken = () =>
    new Promise<string | null>((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (tokenRef.current) return resolve(tokenRef.current);
        if (Date.now() - start > TOKEN_WAIT_MS) return resolve(null);
        window.setTimeout(tick, 200);
      };
      tick();
    });

  const failWith = (error: SubmitError, type: string) => {
    trackEvent("form_submit_error", { form_id: FORM_ID, error_type: type });
    setSubmitError(error);
    tokenRef.current = null;
    setTurnstileKey((k) => k + 1);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    setTouched({ name: true, email: true, phone: true, need: true, message: true });

    const { data, errors: all } = validate(values);
    if (!data) {
      const bad = FIELDS.filter((f) => all[f]);
      setSummary(
        bad.length === 1
          ? t(`Controlla il campo: ${FIELD_NAMES[bad[0]!]}.`, "Check the highlighted field.")
          : t(
              `Controlla ${bad.length} campi: ${bad.map((f) => FIELD_NAMES[f]).join(", ")}.`,
              `Check ${bad.length} fields.`,
            ),
      );
      focusField(bad[0]!);
      return;
    }
    setSummary(null);
    setIsSubmitting(true);

    try {
      const token = turnstileEnabled ? tokenRef.current ?? (await waitForToken()) : null;
      if (turnstileEnabled && !token) {
        failWith(
          {
            kind: "antibot",
            message: t(
              "La verifica anti-bot non si è caricata (a volte è colpa di un blocca-pubblicità). Riprova, oppure scrivimi su WhatsApp o per email.",
              "The anti-bot check did not load (sometimes an ad blocker is the cause). Try again, or message me on WhatsApp or by email.",
            ),
          },
          "antibot",
        );
        return;
      }

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          turnstileToken: token,
          origin: window.location.href,
          referrer: document.referrer || undefined,
          landingContext: { source: "site", formId: "site-contatti" },
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        field?: string;
        userSent?: boolean;
      };

      if (!res.ok) {
        const kind =
          res.status === 429 ? "rate_limit" : res.status === 403 ? "antibot" : "server";
        failWith(
          {
            kind,
            message:
              body.error ||
              t(
                "Non sono riuscito a inviare la richiesta. Non hai perso quello che hai scritto: riprova tra un minuto, oppure scrivimi per email o su WhatsApp.",
                "I couldn't send your request. Nothing you wrote is lost: try again in a minute, or email me or message me on WhatsApp.",
              ),
          },
          res.status === 400 ? "validation" : kind,
        );
        if (FIELDS.includes(body.field as ContactField)) {
          focusField(body.field as ContactField);
        }
        return;
      }

      // The only real conversion: fires after the server accepted the lead.
      // Plausible goal: custom event "form_submission".
      trackEvent("form_submission", {
        form_id: FORM_ID,
        need: data.need,
        has_phone: !!data.phone,
      });
      trackEvent("generate_lead", { currency: "EUR", value: 0 });

      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch {}
      router.replace(
        `${routes.contactsThanks}${body.userSent === false ? "?confirmationSent=0" : ""}`,
      );
    } catch {
      failWith(
        {
          kind: "server",
          message: t(
            "Connessione interrotta. Non hai perso quello che hai scritto: controlla la rete e riprova.",
            "Connection lost. Nothing you wrote is lost: check your network and try again.",
          ),
        },
        "network",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const needLabel = values.need ? NEED_LABELS[values.need][0] : "";
  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    "Richiesta dal sito",
  )}&body=${encodeURIComponent(
    [values.name, values.phone, needLabel, "", values.message].filter(Boolean).join("\n"),
  )}`;

  const inputClass = (f: ContactField) =>
    `h-12 bg-surface-hover border-[hsl(var(--border))] ${errorOf(f) ? "border-destructive" : ""}`;

  const describedBy = (f: ContactField, hasHint: boolean) =>
    [hasHint ? `cf-${f}-hint` : "", errorOf(f) ? `cf-${f}-error` : ""]
      .filter(Boolean)
      .join(" ") || undefined;

  const fieldError = (f: ContactField) =>
    errorOf(f) ? (
      <p id={`cf-${f}-error`} className="text-sm text-destructive">
        {errorOf(f)}
      </p>
    ) : null;

  return (
    <div className="surface-card p-5 md:p-8">
      <div className="mb-6 rounded-2xl border border-primary/25 bg-primary/5 px-5 py-4 text-left">
        <p className="font-display text-xl font-semibold text-primary">
          {t("Raccontami cosa ti serve", "Tell me what you need")}
        </p>
        <p className="mt-1.5 text-sm text-primary/80">
          {t(
            "Ti rispondo io, di persona, entro 24 ore lavorative. Nessun commerciale in mezzo.",
            "I reply personally within 24 business hours. No salespeople in between.",
          )}
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        {summary ? (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {summary}
          </p>
        ) : null}

        <div className="space-y-2">
          <label htmlFor="cf-name" className="text-sm font-medium">
            {t("Come ti chiami?", "What's your name?")}
          </label>
          <Input
            id="cf-name"
            name="name"
            autoComplete="name"
            enterKeyHint="next"
            placeholder={t("Es. Giulia Rossi", "E.g. Jane Smith")}
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            onBlur={() => touch("name")}
            aria-invalid={!!errorOf("name")}
            aria-describedby={describedBy("name", false)}
            className={inputClass("name")}
          />
          {fieldError("name")}
        </div>

        <div className="space-y-2">
          <label htmlFor="cf-email" className="text-sm font-medium">
            {t("La tua email", "Your email")}
          </label>
          <Input
            id="cf-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            enterKeyHint="next"
            placeholder="nome@azienda.it"
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
            onBlur={() => touch("email")}
            aria-invalid={!!errorOf("email")}
            aria-describedby={describedBy("email", true)}
            className={inputClass("email")}
          />
          <p id="cf-email-hint" className="text-xs text-muted-foreground">
            {t("Ti rispondo a questo indirizzo.", "I'll reply to this address.")}
          </p>
          {fieldError("email")}
        </div>

        <div className="space-y-2">
          <label htmlFor="cf-phone" className="text-sm font-medium">
            {t("Telefono o WhatsApp", "Phone or WhatsApp")}{" "}
            <span className="text-muted-foreground">({t("facoltativo", "optional")})</span>
          </label>
          <Input
            id="cf-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            enterKeyHint="next"
            placeholder="333 1234567"
            value={values.phone}
            onChange={(e) => set("phone", e.target.value)}
            onBlur={() => touch("phone")}
            aria-invalid={!!errorOf("phone")}
            aria-describedby={describedBy("phone", true)}
            className={inputClass("phone")}
          />
          <p id="cf-phone-hint" className="text-xs text-muted-foreground">
            {t(
              "Solo se preferisci essere richiamato. Non ti chiamo senza averti prima scritto.",
              "Only if you'd rather get a call. I won't call before writing to you first.",
            )}
          </p>
          {fieldError("phone")}
        </div>

        <fieldset className="space-y-2" aria-describedby={describedBy("need", true)}>
          <legend className="text-sm font-medium">{t("Cosa ti serve?", "What do you need?")}</legend>
          <p id="cf-need-hint" className="text-xs text-muted-foreground">
            {t("Se non sai, scegli l'ultima.", "Not sure? Pick the last one.")}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {contactNeedValues.map((need) => (
              <label
                key={need}
                className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border bg-surface-hover px-4 py-3 text-sm transition-colors hover:border-primary/60 has-[:checked]:border-primary has-[:checked]:bg-primary/10 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring ${
                  errorOf("need") ? "border-destructive" : "border-[hsl(var(--border))]"
                }`}
              >
                <input
                  type="radio"
                  name="need"
                  value={need}
                  checked={values.need === need}
                  onChange={() => {
                    set("need", need);
                    touch("need");
                  }}
                  className="h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
                />
                {NEED_LABELS[need][isItalian ? 0 : 1]}
              </label>
            ))}
          </div>
          {fieldError("need")}
        </fieldset>

        <div className="space-y-2">
          <label htmlFor="cf-message" className="text-sm font-medium">
            {t("Raccontami in due righe", "Tell me in a couple of lines")}
          </label>
          <Textarea
            id="cf-message"
            name="message"
            rows={4}
            placeholder={t(
              "Es. Ho un negozio online che si carica lento e vorrei capire cosa si può fare.",
              "E.g. My online shop loads slowly and I'd like to know what can be done.",
            )}
            value={values.message}
            onChange={(e) => set("message", e.target.value)}
            onBlur={() => touch("message")}
            aria-invalid={!!errorOf("message")}
            aria-describedby={describedBy("message", true)}
            className={`min-h-[7.5rem] max-h-[24rem] resize-y [field-sizing:content] bg-surface-hover border-[hsl(var(--border))] ${
              errorOf("message") ? "border-destructive" : ""
            }`}
          />
          <p id="cf-message-hint" className="text-xs text-muted-foreground">
            {t(
              "Bastano obiettivo, sito attuale (se c'è) ed eventuale scadenza.",
              "Goal, current site (if any) and any deadline are enough.",
            )}
          </p>
          {fieldError("message")}
        </div>

        {turnstileEnabled ? (
          <TurnstileWidget
            key={turnstileKey}
            onToken={handleTurnstileToken}
            action="contact-form"
          />
        ) : null}

        {submitError ? (
          <div
            role="alert"
            className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <p>{submitError.message}</p>
            {submitError.kind !== "rate_limit" ? (
              <p className="flex flex-wrap gap-x-4 gap-y-1 font-medium">
                <a href={mailtoHref} className="underline underline-offset-2">
                  {t("Invia via email", "Send by email")}
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2"
                >
                  {t("Scrivimi su WhatsApp", "Message me on WhatsApp")}
                </a>
              </p>
            ) : null}
          </div>
        ) : null}

        <Button
          type="submit"
          variant="hero"
          size="lg"
          className="h-[52px] w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("Sto inviando...", "Sending...")}
            </>
          ) : (
            <>
              {t("Invia la richiesta", "Send request")}
              <Send className="w-4 h-4" />
            </>
          )}
        </Button>

        <div className="space-y-2 text-center text-sm text-muted-foreground">
          <p>
            {t("Preferisci parlare? ", "Rather talk? ")}
            <a
              href="#tidycal"
              onClick={() =>
                trackEvent("book_call_click", { tool: "tidycal", location: "form_footer" })
              }
              className="text-primary hover:underline"
            >
              {t("Prenota una call di 30 minuti", "Book a 30-minute call")}
            </a>
            {t(" o ", " or ")}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "form_footer" })}
              className="text-primary hover:underline"
            >
              {t("scrivimi su WhatsApp", "message me on WhatsApp")}
            </a>
            .
          </p>
          <p className="text-xs">
            {t("Uso i tuoi dati solo per risponderti, come spiego nell'", "I use your data only to reply to you, as explained in the ")}
            <Link href={routes.privacy} target="_blank" className="text-primary hover:underline">
              {t("informativa privacy", "privacy policy")}
            </Link>
            .
          </p>
        </div>
      </form>
    </div>
  );
}
